import React, { useState, useRef, useEffect } from "react";
import "./login.css";
import { DocketAPIError, getAPIClient } from "../../apiClient";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { errorLog } from "../../utils/logger";
import { DateTime } from "luxon";
import { ErrorModal } from "../../components/modals/ErrorModal";
import { userAtom } from "../../jotai/atoms";
import { useAtom } from "jotai";

export const EnterPhonePinSchema = yup.object().shape({
  pin: yup.string().required().min(6).max(6),
});

export function EnterPhonePin({ autoFocusInput }: { autoFocusInput?: boolean }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pathname, state } = useLocation();
  const [showErrorModal, setShowErrorModal] = useState<string | boolean>();
  const [submittingPin, setSubmittingPin] = useState(false);
  const [currentUser, setCurrentUser] = useAtom(userAtom);
  const phoneNumber = state?.phone as string | undefined;

  const mobilePinInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (autoFocusInput && mobilePinInputRef.current) {
      mobilePinInputRef.current.focus();
    }
  }, [autoFocusInput]);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty, isSubmitting },
    getValues,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(EnterPhonePinSchema),
  });

  const pin = watch("pin");

  const { ref: registerMobileRef, ...registerMobileRest } = register("pin");

  const searchFormTranslations = {
    dateOfBirth: t("immunizations.search_my_dob_label"),
    legalSex: t("immunizations.search_my_legal_sex_label"),
  };

  const formattedNumber = () => {
    if (!phoneNumber) return "";

    const match = phoneNumber.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
    if (!match) return "";
    const number = ["+1 (", match[2], ") ", match[3], "-", match[4]].join("");
    return number;
  };

  const onSubmit = async (data: any, navigateUrl?: string) => {
    if (!data || !data.pin) {
      return;
    }

    try {
      setSubmittingPin(true);
      await getAPIClient().verifyUserAccountPhone({ code: data.pin });
      // We re-retrieve the user here since the verified phone number may now be set
      const user = await getAPIClient().getUser();
      if (!user?.verified_phone_number) {
        throw new DocketAPIError(t("onboarding.verify-enter-pin-error"));
      }

      await setCurrentUser(user);
      if (navigateUrl) navigate(navigateUrl);
    } catch (e: unknown) {
      errorLog(e as Error);
      if (e instanceof DocketAPIError) {
        setShowErrorModal(e.message);
      }
    } finally {
      setSubmittingPin(false);
    }
  };

  const getEnterPinLayout = () => {
    // TODO: Pull out the core bit of this into a separate component.
    if (pathname === "/signup/enterpin" || pathname === "/home/settings/pin") {
      // sign up flow
      return (
        <>
          {showErrorModal && (
            <ErrorModal onCloseModal={() => setShowErrorModal(false)}>{showErrorModal}</ErrorModal>
          )}
          <form
            onSubmit={
              pathname === "/signup/enterpin"
                ? handleSubmit((data) => onSubmit(data, "/home/search"))
                : handleSubmit((data) => onSubmit(data, "/home/settings"))
            }
            className="flex flex-col h-full"
          >
            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto px-2">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col text-center">
                  <p className="text-xl">{t("onboarding.verify-enter-pin-desc")}</p>
                  <p className="text-xl">{formattedNumber()}</p>
                  <p className="text-sm">{t("onboarding.verify-enter-pin-desc2")}</p>
                </div>
                {/* Mobile input */}
                <div className="md:hidden w-full">
                  <input
                    {...registerMobileRest}
                    className="input input-primary w-full"
                    type="tel"
                    inputMode="tel"
                    placeholder="Enter Code"
                    data-testid="pin-mobile"
                    maxLength={6}
                    ref={(el) => {
                      registerMobileRef(el);
                      if (mobilePinInputRef) mobilePinInputRef.current = el;
                    }}
                  />
                </div>
                {/* Desktop input and buttons */}
                <fieldset className="fieldset max-md:hidden">
                  <legend className="fieldset-legend pt-0 text-[16px] font-[700] md:text-black">
                    {t("settings.enter_pin")}:
                  </legend>
                  <input
                    className="input input-primary w-full"
                    type="text"
                    placeholder="Enter Code"
                    data-testid="pin-desktop"
                    maxLength={6}
                    value={pin}
                    onChange={(e) => setValue("pin", e.target.value, { shouldValidate: true })}
                  />
                </fieldset>
                <div className="flex flex-col max-md:hidden gap-2">
                  <button
                    className="btn w-full"
                    type="submit"
                    disabled={!isValid || submittingPin}
                    data-testid="submit-desktop"
                  >
                    {t("generic.submit")}
                  </button>
                  <button className="btn btn-ghost w-full" onClick={() => navigate("/home/search")}>
                    {t("generic.skip")}
                  </button>
                </div>
              </div>
            </div>
            {/* Mobile buttons anchored at bottom */}
            <div className="shrink-0 max-md:pb-8 md:hidden flex flex-col gap-4 pt-4 px-2">
              <button
                disabled={!isValid || submittingPin}
                className="btn btn-primary btn-lg w-full"
                type="submit"
                data-testid="submit-mobile"
              >
                {t("generic.submit")}
              </button>
              {pathname === "/signup/enterpin" && (
                <button
                  className="btn btn-lg btn-ghost w-full"
                  onClick={() => navigate("/home/search")}
                >
                  {t("generic.skip")}
                </button>
              )}
            </div>
          </form>
        </>
      );
    } else if (pathname === "/home/settings") {
      // settings update phone
      return (
        <>
          {showErrorModal && (
            <ErrorModal onCloseModal={() => setShowErrorModal(false)}>{showErrorModal}</ErrorModal>
          )}
          <form onSubmit={handleSubmit((data) => onSubmit(data, "/home/settings"))}>
            <div className="input-container">
              <label className="label text-dark dark:text-white">{t("settings.enter_pin")}:</label>
              <input
                className="input max-md:w-full text-black dark:text-white"
                type="text"
                placeholder="123456"
                {...register("pin")}
              />
            </div>
            <button className="mt-4 btn" type="submit" disabled={!isValid || submittingPin}>
              {submittingPin ? (
                <span className="loading loading-spinner loading-sm"></span>
              ) : (
                t("generic.submit")
              )}
            </button>
          </form>
        </>
      );
    } else {
      throw new Error("Sorry, this is not supported");
    }
  };

  return <>{getEnterPinLayout()}</>;
}

export default EnterPhonePin;
